import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import { useListComponentsQuery } from '../../api/authoring'
import type { Manifest } from '../../api/authoring'
import { LISTING_LAG } from '../../model/labels'
import { inlineable, typeProse } from '../../model/manifest'
import { CreateComponentDialog } from '../../ui/CreateComponentDialog'
import { InlineSetup } from '../../ui/InlineSetup'
import { Loading, QueryError } from '../../ui/QueryState'

/**
 * Add a component to a region.
 *
 * Two ways, because the model has two, and the manifest says which types
 * allow which:
 *
 * **Link an existing component.** A linked child names a target that
 * must already exist — the surface reads its type off the target and
 * *refuses* a stated one — so this browses the same listing the site
 * list boots on, with a different `?type=`. That is why the picker is
 * not an optional nicety.
 *
 * **Define one inline.** An inline child has no identity and no stream:
 * it versions, pins and travels with its host, and it is how most of a
 * page is actually built. A type carrying identity — Media, whose
 * component id *is* the library identity of an uploaded file — declares
 * `inlineable: false` and is offered only as a link.
 *
 * **Or make a new one.** A linked child must name a component that
 * exists, so a library with nothing in it used to be a dead end — the
 * author had to go and create one, and creation had no home at all. It
 * does now, and it is offered here rather than only in the library
 * because "there is no Content to place" and "I want a new Content" are
 * one moment. What comes back is placed directly, never looked up: the
 * listing is served from the projection and lags, so a component created
 * a second ago is reliably absent from it.
 *
 * There is no region control here. The region is whichever one's **Add**
 * was pressed, and every declared region has one even when it is empty —
 * which is what the declared vocabulary bought (B14). Asking twice would
 * be a second value to disagree with the first.
 */
export function PlacementPicker({
  tenant,
  into,
  palette,
  manifest,
  onClose,
  onLink,
  onInline,
  busy,
}: {
  tenant: string
  /**
   * What is being added to, or null when the dialog is closed. A region
   * name where the holder has more than one, and the holder's own type
   * where it keeps a single unnamed list — the same words the region
   * heading uses, so the dialog names what the author pressed.
   */
  into: string | null
  /** The types the holding node may contain, from the manifest. */
  palette: string[]
  manifest: Manifest | undefined
  onClose: () => void
  onLink: (targetId: string) => void
  /**
   * A child defined in place: the type, and the fields its first
   * revision must state where the type declares any.
   */
  onInline: (typeName: string, fields: Record<string, unknown>) => void
  busy: boolean
}) {
  const open = into !== null
  const inlinePalette = palette.filter((name) => inlineable(manifest, name))

  const [mode, setMode] = useState<'link' | 'inline'>('link')
  const [type, setType] = useState(palette[0] ?? '')
  const [creating, setCreating] = useState(false)

  // Reset when the dialog opens. Without this the controls keep whatever
  // the previous open left them on — and initialising from props at first
  // mount alone reads them before there is anything to read.
  useEffect(() => {
    if (open) {
      setMode('link')
      setType(palette[0] ?? '')
      setCreating(false)
    }
  }, [open, palette])

  const listing = useListComponentsQuery({ tenant, type, limit: 100 }, { skip: !open || !type || mode !== 'link' })
  const components = listing.data?.components ?? []

  // What the chosen type calls itself. Every sentence below that used to
  // print a wire name prints this instead — "Make a new photo gallery"
  // rather than "Make a new Gallery" — and a type that declares no prose
  // still reads as a humanised version of its own name.
  const prose = typeProse(manifest, type)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Add to <code>{into}</code>
      </DialogTitle>

      <Tabs
        value={mode}
        onChange={(_event, next: 'link' | 'inline') => {
          setMode(next)
          setType((next === 'inline' ? inlinePalette : palette)[0] ?? '')
        }}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="link" label="Link an existing component" />
        <Tab value="inline" label="Define one inline" disabled={inlinePalette.length === 0} />
      </Tabs>

      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            select
            label="Type"
            value={type}
            onChange={(event) => setType(event.target.value)}
            sx={{ maxWidth: 240 }}
            helperText="What this node may hold, per the manifest."
          >
            {(mode === 'inline' ? inlinePalette : palette).map((name) => (
              <MenuItem key={name} value={name}>
                {typeProse(manifest, name).title}
              </MenuItem>
            ))}
          </TextField>

          {mode === 'inline' ? (
            <>
              <Alert severity="info">
                An inline <strong>{prose.title.toLowerCase()}</strong> has no identity and no stream of its own — it
                versions and pins with this page, and cannot be linked from anywhere else. It is created empty and
                configured here afterwards, unless its type cannot be empty and asks below.
              </Alert>
              <InlineSetup
                manifest={manifest}
                typeName={type}
                label={`Add inline ${prose.title.toLowerCase()}`}
                busy={busy}
                onPlace={(fields) => onInline(type, fields)}
              />
            </>
          ) : (
            <>
              {listing.isLoading && <Loading label="Listing components…" />}
              {listing.error && <QueryError error={listing.error} title="Could not list components" />}

              {!listing.isLoading && !listing.error && components.length === 0 && (
                <Alert severity="info">
                  No {prose.plural.toLowerCase()} in this tenant.{' '}
                  {inlineable(manifest, type)
                    ? 'Every one in this site is defined inline — try the other tab, or make a new standalone one.'
                    : 'A linked child must name one that exists, because that is where its type comes from.'}
                </Alert>
              )}

              <Stack direction="row">
                <Button
                  startIcon={<AddIcon />}
                  disabled={!type || busy}
                  onClick={() => setCreating(true)}
                >
                  Make a new {prose.title.toLowerCase()}
                </Button>
              </Stack>

              {components.length > 0 && (
                <List dense disablePadding sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {components.map((component) => (
                    <ListItemButton key={component.id} disabled={busy} onClick={() => onLink(component.id)}>
                      <ListItemText primary={component.label ?? component.id} secondary={component.id} />
                    </ListItemButton>
                  ))}
                </List>
              )}

              <Typography variant="caption" color="text.secondary">
                {LISTING_LAG}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>

      {/* Placed straight from the create's own answer. Re-listing to find
          it would race the projection and, more often than not, come back
          without it. */}
      <CreateComponentDialog
        tenant={tenant}
        open={creating}
        manifest={manifest}
        types={[type]}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false)
          onLink(created.id)
        }}
      />
    </Dialog>
  )
}
